// hooks/useExtendDuration.js
import { useState, useCallback } from 'react';
import { useConfig } from 'wagmi';
import { writeContract, waitForTransactionReceipt, estimateGas, getGasPrice } from 'wagmi/actions';
import { formatEther } from 'viem';
import { childPool } from '../../../../utils/contract';

export const useExtendDuration = () => {
    const [isExtendInProgress, setIsExtendInProgress] = useState(false);
    const [isEstimatingGas, setIsEstimatingGas] = useState(false);
    const config = useConfig();

    const estimateExtendGas = useCallback(
        async (poolContractAddress, increaseTimeInSeconds) => {
            try {
                setIsEstimatingGas(true);

                // console.log("Estimating gas for time increase (seconds):", increaseTimeInSeconds);

                // Estimate gas for the transaction
                const estimatedGas = await estimateGas(config, {
                    address: poolContractAddress,
                    abi: childPool?.abi,
                    functionName: 'increaseRewardEndTime',
                    args: [increaseTimeInSeconds],
                });

                // Get current gas price
                const gasPrice = await getGasPrice(config);

                // Calculate total gas cost in wei
                const totalGasCost = estimatedGas * gasPrice;

                // Convert to ETH
                const totalGasCostETH = formatEther(totalGasCost);

                return {
                    estimatedGas: estimatedGas?.toString(),
                    gasPrice: gasPrice?.toString(),
                    totalGasCostWei: totalGasCost?.toString(),
                    totalGasCostETH: parseFloat(totalGasCostETH),
                    totalGasCostETHFormatted: totalGasCostETH
                };

            } catch (error) {
                console.error("Error estimating gas:", error);
                throw error;
            } finally {
                setIsEstimatingGas(false);
            }
        },
        [config]
    );

    const extendDuration = useCallback(
        async (poolContractAddress, increaseTimeInSeconds) => {
            try {
                setIsExtendInProgress(true);

                // console.log("Sending transaction with time increase (seconds):", increaseTimeInSeconds);

                const hash = await writeContract(config, {
                    address: poolContractAddress,
                    abi: childPool?.abi,
                    functionName: 'increaseRewardEndTime',
                    args: [increaseTimeInSeconds],
                });

                const receipt = await waitForTransactionReceipt(config, { hash });

                // console.log("Duration extension successful. Transaction hash:", hash);

                return { receipt, hash };

            } catch (error) {
                console.error("Error extending duration:", error);
                throw error;
            } finally {
                setIsExtendInProgress(false);
            }
        },
        [config]
    );

    return {
        isExtendInProgress,
        isEstimatingGas,
        extendDuration,
        estimateExtendGas
    };
};